import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import { BottomModalProps } from '../components';
import { SelectCustomers } from '../components/select-customers/select-customers';
import { SelectIntent } from '../components/select-intent/select-intent';
import { DeleteMessage } from '../components/delete-message/delete-message';
import {
  GetTripSummaryResponse,
  getUserData,
  getWorkspaceId,
  useAppSelector,
  useLazyGetTripSummaryQuery,
} from '@zonofi/zono-money-store';
import moment from 'moment';

enum TAB {
  PENDING = 'PENDING',
  PROCESSED = 'PROCESSED',
}

interface FieldOpsUploadSummaryScreenContextValue extends BottomModalProps {
  openModal: (config: Partial<BottomModalProps>) => void;
  closeModal: () => void;
  openSelectCustomerModal: () => void;
  openChangeIntentModal: () => void;
  openDeleteModal: () => void;
  getTripSummaryAPIResponse: {
    data?: GetTripSummaryResponse[];
    isLoading: boolean;
    isError: boolean;
  };
  selectedTab: TAB;
  setSelectedTab: (tab: TAB) => void;
  tabs: { id: TAB; value: string }[];
  renderTabs: () => ReactNode;
}

const FieldOpsUploadSummaryScreenContext = createContext<
  FieldOpsUploadSummaryScreenContextValue | undefined
>(undefined);

const initialModalState: BottomModalProps = {
  isVisible: false,
  title: '',
  children: null,
  leftText: '',
  onLeftAction: undefined,
  showHeader: false,
  showDivider: false,
  bottomButton: '',
  onBottomAction: () => {},
};
export const FieldOpsUploadSummaryScreenProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const teamMemberId = useAppSelector(getUserData).inviteId || '';
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [getTripSummaryAPI, getTripSummaryAPIResponse] =
    useLazyGetTripSummaryQuery();

  const [modalState, setModalState] =
    useState<BottomModalProps>(initialModalState);
  const [selectedTab, setSelectedTab] = useState<TAB>(TAB.PENDING);

  const tabs = [
    {
      id: TAB.PENDING,
      value: 'Pending',
    },
    { id: TAB.PROCESSED, value: 'Processed' },
  ];

  const renderTabs = useCallback(
    () => (
      <div className="upload-summary-tabs font-regular">
        {tabs.map((tab, idx) => (
          <div
            key={tab.id}
            className={`upload-summary-tab ${
              selectedTab === tab.id ? 'active' : ''
            }`}
            onClick={() => setSelectedTab(tab.id)}
          >
            {tab.value}
          </div>
        ))}
      </div>
    ),
    [selectedTab]
  );

  const openModal = (config: Partial<BottomModalProps>) => {
    setModalState({
      ...modalState,
      ...config,
      isVisible: true,
    });
  };

  const closeModal = () => {
    setModalState({ ...initialModalState });
  };

  const openSelectCustomerModal = () => {
    openModal({
      title: 'Select Customer',
      children: <SelectCustomers />,
      leftText: 'Close',
      onLeftAction: closeModal,
      showHeader: true,
      showDivider: true,
      bottomButton: 'Assign',
      onBottomAction: () => {},
    });
  };

  const openChangeIntentModal = () => {
    openModal({
      title: 'Change Intent',
      children: <SelectIntent />,
      leftText: 'Close',
      onLeftAction: closeModal,
      showHeader: true,
      showDivider: true,
      bottomButton: 'Change',
      onBottomAction: () => {},
    });
  };

  const openDeleteModal = () => {
    openModal({
      children: <DeleteMessage />,
      onLeftAction: closeModal,
      showHeader: false,
      showDivider: false,
    });
  };

  const fetchTripSummaryData = useCallback(() => {
    getTripSummaryAPI({
      params: {
        sellerWorkspaceId: sellerWorkspaceId,
        date: moment(new Date()).format('YYYY-MM-DD'),
        status: selectedTab,
        teamMemberId: teamMemberId,
      },
    });
  }, [selectedTab]);

  useEffect(() => {
    fetchTripSummaryData();
  }, [selectedTab]);

  return (
    <FieldOpsUploadSummaryScreenContext.Provider
      value={{
        ...modalState,
        openModal,
        closeModal,
        openSelectCustomerModal,
        openChangeIntentModal,
        openDeleteModal,
        selectedTab,
        setSelectedTab,
        tabs,
        renderTabs,
        getTripSummaryAPIResponse: {
          data: getTripSummaryAPIResponse?.data,
          isError: getTripSummaryAPIResponse?.isError,
          isLoading: getTripSummaryAPIResponse?.isLoading,
        },
      }}
    >
      {children}
    </FieldOpsUploadSummaryScreenContext.Provider>
  );
};

export const useFieldOpsUploadSummaryScreen =
  (): FieldOpsUploadSummaryScreenContextValue => {
    const ctx = useContext(FieldOpsUploadSummaryScreenContext);
    if (!ctx) {
      throw new Error(
        'useFieldOpsUploadSummaryScreen must be used within FieldOpsUploadSummaryScreenProvider'
      );
    }
    return ctx;
  };
